import { GreenerAssetFilter } from '../GreenerAssetFilter';
import { OrganisationFilter } from '../OrganisationFilter';
import { ProjectStatusFilter } from '../ProjectStatusFilter';
import { ProjectTagsFilter } from '../ProjectTagsFilter';
import { baseTagNameFilter } from '../ProjectTagsFilter';
import {
  Badge,
  Button,
  Grid,
  Stack,
  ToggleButton,
  Typography,
} from '@mui/material';
import { useFilter } from 'features/filters/hooks/useFilter';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useState } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Project } from 'types';
import { Icons, SearchInput } from 'greener-ui-library';

const baseProjectNameFilter = {
  filterKey: 'name__contains',
};

const createProjectNameFilter = (value) => ({
  ...baseProjectNameFilter,
  value,
});

export const ProjectsFilterSection = ({ displayOrganisationFilter }) => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const { isMobile } = useBreakpoint();
  // Use the useFilter hook to manage filters
  const { updateFilters, getFilterValues, clearFilter, clearAllFilters, getFilters, applyFilters } = useFilter<Project>(
    {
      isValidFilterProperty: (property) =>
        [
          'greener_unit__external_identifier',
          'project_organisation__name',
          'name',
          'status',
          'ts_end',
          'ts_start',
          'is_active',
          'conjunction',
          'tag_name',
        ].includes(property),
    }
  );

  const projectSearchValue = getFilterValues?.(baseProjectNameFilter.filterKey)?.[0] ?? '';
  const [resetKey, setResetKey] = useState(0);
  const { t } = useTranslation();

  //re-apply filters when changing views
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleProjectNameSearchChange = (val: string) => {
    if (val.trim() === '') {
      handleClearSearchInput();
    } else {
      const newProjectSearchFilter = createProjectNameFilter(val);
      updateFilters([newProjectSearchFilter]);
    }
  };

  const handleClearSearchInput = () => {
    clearFilter(baseProjectNameFilter.filterKey);
  };

  const onClearAll = () => {
    setResetKey(key => key + 1); // force re-render to clean state of SearchInput
    clearAllFilters();
    handleClearSearchInput();
  };

  const props = {
    handleProjectNameSearchChange,
    projectSearchValue,
    getFilters,
    getFilterValues,
    updateFilters,
    clearFilter,
    onClearAll,
    resetKey,
    t,
    showMoreFilters,
    setShowMoreFilters,
    displayOrganisationFilter,
  };

  return isMobile ? <MobileLayout {...props} /> : <DesktopLayout {...props} />;
};

const DesktopLayout = ({
  handleProjectNameSearchChange,
  projectSearchValue,
  getFilters,
  getFilterValues,
  updateFilters,
  clearFilter,
  onClearAll,
  t,
  resetKey,
  showMoreFilters,
  setShowMoreFilters,
  displayOrganisationFilter,
}) => {
  return (
    <Grid item container xs={12} md={11} columnSpacing={1.5} rowSpacing={2}>
      <Grid item xs={6} md={showMoreFilters ? 1.5 : 2} sx={{ minWidth: '180px' }}>
        <SearchInput
          key={resetKey}
          hasStartAdornment={false}
          defaultValue={projectSearchValue}
          onDebouncedChange={handleProjectNameSearchChange}
          placeholder={t('blueprints:card.filterSection.searchBlueprint')}
          fullWidth
        />
      </Grid>

      {displayOrganisationFilter && (
        <Grid item xs={6} md={showMoreFilters ? 1.5 : 2} sx={{ minWidth: '180px' }}>
          <OrganisationFilter
            updateFilters={updateFilters}
            clearFilter={clearFilter}
            getFilterValues={getFilterValues}
            filterKey="related_organisation__name__eq"
          />
        </Grid>
      )}

      <Grid item xs={6} md={showMoreFilters ? 1.5 : 2} sx={{ minWidth: '180px' }}>
        <GreenerAssetFilter updateFilters={updateFilters} clearFilter={clearFilter} getFilterValues={getFilterValues} />
      </Grid>

      <Grid item xs={6} md={showMoreFilters ? 1.5 : 2} sx={{ minWidth: '180px' }}>
        <ProjectStatusFilter getFilters={getFilters} updateFilters={updateFilters} clearFilter={clearFilter} />
      </Grid>

      {showMoreFilters && (
        <ProjectTagsFilter
          getFilterValues={getFilterValues}
          updateFilters={updateFilters}
          gridSize={{ xs: 6, md: showMoreFilters ? 1.5 : 2 }}
        />
      )}

      <Grid
        item
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <MoreFiltersButton
          showMoreFilters={showMoreFilters}
          setShowMoreFilters={setShowMoreFilters}
          getFilterValues={getFilterValues}
        />
        <ClearAllButton getFilters={getFilters} onClearAll={onClearAll} />
      </Grid>
    </Grid>
  );
};

const MobileLayout = ({
  handleProjectNameSearchChange,
  projectSearchValue,
  getFilters,
  getFilterValues,
  updateFilters,
  clearFilter,
  onClearAll,
  t,
  resetKey,
  showMoreFilters,
  setShowMoreFilters,
  displayOrganisationFilter,
}) => {
  return (
    <>
      <Grid item container xs={12} md={9} columnSpacing={1.5} rowSpacing={2}>
        <Grid item xs={12}>
          <SearchInput
            key={resetKey}
            defaultValue={projectSearchValue}
            onDebouncedChange={handleProjectNameSearchChange}
            placeholder={t('blueprints:card.filterSection.searchBlueprint')}
            fullWidth
          />
        </Grid>

        {showMoreFilters && (
          <>
            <Grid item xs={12} md={2}>
              <GreenerAssetFilter
                updateFilters={updateFilters}
                clearFilter={clearFilter}
                getFilterValues={getFilterValues}
              />
            </Grid>
            {displayOrganisationFilter && (
              <Grid item xs={6} md={2}>
                <OrganisationFilter
                  updateFilters={updateFilters}
                  clearFilter={clearFilter}
                  getFilterValues={getFilterValues}
                  filterKey="related_organisation__name__eq"
                />
              </Grid>
            )}
            <Grid item xs={6} md={2}>
              <ProjectStatusFilter getFilters={getFilters} updateFilters={updateFilters} clearFilter={clearFilter} />
            </Grid>
            <ProjectTagsFilter
              getFilterValues={getFilterValues}
              updateFilters={updateFilters}
              gridSize={{ xs: 6, md: 2 }}
            />
          </>
        )}
      </Grid>

      <Grid item xs={8}>
        <Stack direction="row" alignItems={'center'} justifyContent={'flex-start'} gap={1}>
          <MoreFiltersButton
            showMoreFilters={showMoreFilters}
            setShowMoreFilters={setShowMoreFilters}
            getFilterValues={getFilterValues}
          />

          <ClearAllButton getFilters={getFilters} onClearAll={onClearAll} />
        </Stack>
      </Grid>
    </>
  );
};

const MoreFiltersButton = ({ showMoreFilters, setShowMoreFilters, getFilterValues }) => {
  const { t } = useTranslation();
  const [tagFilterValues] = getFilterValues(baseTagNameFilter.filterKey);
  const { isMobile } = useBreakpoint();

  return (
    <ToggleButton
      value={showMoreFilters}
      size="small"
      onClick={() => setShowMoreFilters((prev) => !prev)}
      sx={{ border: 'none', m: 0, px: isMobile ? 0 : 'auto' }}
    >
      <Badge color="primary" variant="dot" invisible={showMoreFilters || !tagFilterValues}>
        <Stack direction="row" spacing={1}>
          <Icons.FilterList fontSize="small" />
          <Typography sx={{ fontWeight: 600, fontSize: '14px', alignSelf: 'center' }}>
            {!showMoreFilters
              ? t('projects:overview.filterSection.moreFilters')
              : t('projects:overview.filterSection.fewerFilters')}
          </Typography>
        </Stack>
      </Badge>
    </ToggleButton>
  );
};

const ClearAllButton = ({ getFilters, onClearAll }) => {
  const { t } = useTranslation();
  return (
    <Button type="text" color="primary" disabled={!getFilters().length} onClick={onClearAll}>
      <Typography variant="body2" sx={{ textTransform: 'none' }}>
        {t('projects:overview.filterSection.clearAll')}
      </Typography>
    </Button>
  );
};
